// Buy box component for service detail page

import { Add, CheckCircleOutline, RadioButtonUnchecked, Remove } from '@mui/icons-material';
import { Box, Button, Chip, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { MarketplaceService } from '../../types/pricing';
import {
  clampAdditionalUsers,
  getAdditionalUserOffer,
  getAdditionalUserOneTimeTotalCents,
  getAdditionalUserTotalCents,
} from '../../lib/services/additionalUsers';
import {
  getAvailableOneTimeAddons,
  getAvailableRecurringBoltOns,
  getOneTimeAddonsTotalCents,
  getRecurringBoltOnsTotalCents,
  normalizeOneTimeAddonSelections,
  normalizeRecurringBoltOnSelections,
} from '../../lib/services/configuredAddons';
import BuyBox from '../marketplace/BuyBox';

interface ServiceBuyBoxProps {
  service: MarketplaceService;
  selectedVariant: string | undefined;
  selectedVariantPrice: number | undefined;
  selectedTerm: number | null;
  selectedFrequency: string | undefined;
  addonPriceCents?: number;
  addonLabel?: string;
}

const formatMonthly = (cents: number) => `$${(cents / 100).toLocaleString('en-US')}/mo`;

const ServiceBuyBox: React.FC<ServiceBuyBoxProps> = ({
  service,
  selectedVariant,
  selectedVariantPrice,
  selectedTerm,
  selectedFrequency,
  addonPriceCents = 0,
  addonLabel,
}) => {
  const [additionalUsers, setAdditionalUsers] = useState(0);
  const [recurringBoltOnSelections, setRecurringBoltOnSelections] = useState<Array<{ addon_key: string }>>([]);
  const [oneTimeAddonSelections, setOneTimeAddonSelections] = useState<Array<{ addon_key: string; quantity: number }>>([]);

  const variantKey = selectedVariant || service.default_variant_key;

  const offer = useMemo(
    () => getAdditionalUserOffer(service, variantKey, selectedTerm, additionalUsers),
    [service, variantKey, selectedTerm, additionalUsers],
  );
  const quantityUnitLabel = offer.unitLabel || 'user';
  const quantityUnitLabelPlural = additionalUsers === 1 ? quantityUnitLabel : `${quantityUnitLabel}s`;

  useEffect(() => {
    if (!offer.enabled) {
      setAdditionalUsers(0);
    }
  }, [offer.enabled, variantKey]);

  const availableRecurringBoltOns = useMemo(
    () => getAvailableRecurringBoltOns(service, variantKey),
    [service, variantKey],
  );

  const availableOneTimeAddons = useMemo(
    () => getAvailableOneTimeAddons(service, variantKey),
    [service, variantKey],
  );

  useEffect(() => {
    setRecurringBoltOnSelections((current) => {
      const allowedKeys = new Set(availableRecurringBoltOns.map((addon) => addon.key));
      const allowedDefinitions = availableRecurringBoltOns;
      const byGroup = new Map<string, string>();
      const next: Array<{ addon_key: string }> = [];

      normalizeRecurringBoltOnSelections(current).forEach((selection) => {
        if (!allowedKeys.has(selection.addon_key)) return;
        const definition = allowedDefinitions.find((addon) => addon.key === selection.addon_key);
        if (!definition) return;
        if (definition.group) {
          if (byGroup.has(definition.group)) return;
          byGroup.set(definition.group, definition.key);
        }
        next.push(selection);
      });

      return next;
    });
  }, [availableRecurringBoltOns]);

  useEffect(() => {
    setOneTimeAddonSelections((current) => {
      const allowedKeys = new Set(availableOneTimeAddons.map((addon) => addon.key));
      return normalizeOneTimeAddonSelections(current).filter((selection) => allowedKeys.has(selection.addon_key));
    });
  }, [availableOneTimeAddons]);

  const handleAdditionalUsersChange = (nextValue: number) => {
    setAdditionalUsers(clampAdditionalUsers(nextValue, offer.maxQuantity));
  };

  const handleRecurringBoltOnToggle = (addonKey: string) => {
    const addon = availableRecurringBoltOns.find((entry) => entry.key === addonKey);
    if (!addon) return;

    setRecurringBoltOnSelections((current) => {
      const normalized = normalizeRecurringBoltOnSelections(current);
      const alreadySelected = normalized.some((selection) => selection.addon_key === addonKey);
      if (alreadySelected) {
        return normalized.filter((selection) => selection.addon_key !== addonKey);
      }

      const withoutGroup = addon.group
        ? normalized.filter((selection) => {
            const selectedAddon = availableRecurringBoltOns.find((entry) => entry.key === selection.addon_key);
            return selectedAddon?.group !== addon.group;
          })
        : normalized;

      return [...withoutGroup, { addon_key: addonKey }];
    });
  };

  const handleOneTimeAddonQuantityChange = (addonKey: string, nextQuantity: number) => {
    const addon = availableOneTimeAddons.find((entry) => entry.key === addonKey);
    if (!addon) return;

    const clampedQuantity = Math.max(0, Math.min(Math.floor(nextQuantity || 0), addon.max_per_order || 1));

    setOneTimeAddonSelections((current) => {
      const nextSelections = normalizeOneTimeAddonSelections(current).filter(
        (selection) => selection.addon_key !== addonKey,
      );

      if (clampedQuantity > 0) {
        nextSelections.push({ addon_key: addonKey, quantity: clampedQuantity });
      }

      return nextSelections;
    });
  };

  const explicitMinPriceCents = Number(service.price_min_cents ?? service.price_range_min);
  const explicitBasePriceCents = Number(service.price_cents ?? service.base_price);
  const serviceLevelFallbackCents =
    Number.isFinite(explicitMinPriceCents) && explicitMinPriceCents > 0
      ? explicitMinPriceCents
      : Number.isFinite(explicitBasePriceCents) && explicitBasePriceCents > 0
        ? explicitBasePriceCents
        : 0;
  const basePriceCents =
    selectedVariantPrice || service.default_pricing?.recurring_cents || serviceLevelFallbackCents || 0;
  const addonBundlePriceCents = addonPriceCents || 0;
  const additionalUserTotalCents = getAdditionalUserTotalCents(service, variantKey, additionalUsers, selectedTerm);
  const additionalUserOneTimeTotalCents = getAdditionalUserOneTimeTotalCents(service, variantKey, additionalUsers, selectedTerm);
  const recurringBoltOnsTotalCents = getRecurringBoltOnsTotalCents(service, variantKey, recurringBoltOnSelections);
  const oneTimeAddonsTotalCents = getOneTimeAddonsTotalCents(service, variantKey, oneTimeAddonSelections);
  const baseRecurringCents = offer.pricingMode === 'absolute_tiered' ? 0 : basePriceCents;
  const totalPriceCents = baseRecurringCents + addonBundlePriceCents + additionalUserTotalCents + recurringBoltOnsTotalCents;

  const serviceConfiguration = {
    ...(offer.enabled ? { additional_users: additionalUsers } : {}),
    ...(recurringBoltOnSelections.length > 0 ? { recurring_bolt_ons: normalizeRecurringBoltOnSelections(recurringBoltOnSelections) } : {}),
    ...(oneTimeAddonSelections.length > 0 ? { one_time_addons: normalizeOneTimeAddonSelections(oneTimeAddonSelections) } : {}),
  };

  const normalizedServiceConfiguration = Object.keys(serviceConfiguration).length > 0 ? serviceConfiguration : undefined;

  const selectedVariantSelections =
    service.variants?.attribute1_name && offer.variant?.attribute1_value
      ? { [service.variants.attribute1_name]: offer.variant.attribute1_value }
      : {};

  const getFrequencyDisplay = (frequency: string): string => {
    switch (frequency) {
      case 'monthly':
        return 'Monthly';
      case 'quarterly':
        return 'Quarterly';
      case 'annually':
        return 'Annually';
      case 'one_time':
        return 'One-time';
      default:
        return frequency;
    }
  };

  return (
    <Grid item xs={12} md={4} sx={{ minWidth: { md: 320 } }}>
      {(selectedTerm || selectedFrequency) && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectedTerm && (
            <Chip label={`${selectedTerm} month term`} size="small" color="primary" variant="filled" />
          )}
          {selectedFrequency && (
            <Chip label={getFrequencyDisplay(selectedFrequency)} size="small" color="secondary" variant="filled" />
          )}
        </Box>
      )}

      {(addonBundlePriceCents > 0 || offer.enabled || availableRecurringBoltOns.length > 0 || availableOneTimeAddons.length > 0) && (
        <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Base plan</Typography>
            <Typography variant="body2">{formatMonthly(baseRecurringCents)}</Typography>
          </Box>

          {addonBundlePriceCents > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">+ {addonLabel || 'Add-ons'}</Typography>
              <Typography variant="body2">{formatMonthly(addonBundlePriceCents)}</Typography>
            </Box>
          )}

          {offer.enabled && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.75, fontWeight: 700 }}>
                {offer.pricingMode === 'absolute_tiered' ? 'Quantity pricing' : 'Recurring quantity add-on'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {offer.pricingMode === 'absolute_tiered'
                  ? `Enter the total ${quantityUnitLabel}${quantityUnitLabel === 'line' ? 's' : ' quantity'} you need. Pricing automatically applies the correct per-${quantityUnitLabel} rate for the selected term and line-count tier.`
                  : `${offer.variant?.display_name || 'Selected plan'} includes ${offer.includedUsers} ${offer.includedUsers === 1 ? quantityUnitLabel : `${quantityUnitLabel}s`}. Add more at ${formatMonthly(offer.pricePerUserCents)} each${offer.maxQuantity ? `, up to ${offer.maxQuantity}` : ''}.`}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                <IconButton aria-label={`remove ${quantityUnitLabel}`} onClick={() => handleAdditionalUsersChange(additionalUsers - 1)} disabled={additionalUsers <= 0}>
                  <Remove />
                </IconButton>
                <TextField
                  label={offer.pricingMode === 'absolute_tiered' ? `Total ${quantityUnitLabelPlural}` : `Extra ${quantityUnitLabelPlural}`}
                  type="number"
                  size="small"
                  value={additionalUsers}
                  onChange={(event) => handleAdditionalUsersChange(Number(event.target.value))}
                  inputProps={{ min: 0, step: 1, max: offer.maxQuantity ?? undefined }}
                  sx={{ width: 140 }}
                />
                <IconButton aria-label={`add ${quantityUnitLabel}`} onClick={() => handleAdditionalUsersChange(additionalUsers + 1)} disabled={Boolean(offer.maxQuantity && additionalUsers >= offer.maxQuantity)}>
                  <Add />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">
                  {offer.pricingMode === 'absolute_tiered' ? `${additionalUsers} ${quantityUnitLabelPlural}` : `+ ${additionalUsers} extra ${quantityUnitLabelPlural}`}
                </Typography>
                <Typography variant="body2">{formatMonthly(additionalUserTotalCents)}</Typography>
              </Box>
              {additionalUserOneTimeTotalCents > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">Setup and configuration</Typography>
                  <Typography variant="body2">${(additionalUserOneTimeTotalCents / 100).toFixed(2)}</Typography>
                </Box>
              )}
            </>
          )}

          {availableRecurringBoltOns.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.75, fontWeight: 700 }}>
                Recurring bolt-ons
              </Typography>
              <Stack spacing={1.25} sx={{ mb: 1 }}>
                {availableRecurringBoltOns.map((addon) => {
                  const isSelected = recurringBoltOnSelections.some((entry) => entry.addon_key === addon.key);
                  return (
                    <Box key={addon.key} sx={{ p: 1.25, border: '1px solid', borderColor: isSelected ? 'primary.main' : 'divider', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {addon.name}
                            {isSelected ? <CheckCircleOutline color="primary" sx={{ fontSize: 16 }} /> : <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.secondary' }} />}
                          </Typography>
                          {addon.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {addon.description}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {formatMonthly(addon.price_cents)} recurring
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => handleRecurringBoltOnToggle(addon.key)}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
              {recurringBoltOnsTotalCents > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">Selected recurring bolt-ons</Typography>
                  <Typography variant="body2">{formatMonthly(recurringBoltOnsTotalCents)}</Typography>
                </Box>
              )}
            </>
          )}

          {availableOneTimeAddons.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.75, fontWeight: 700 }}>
                One-time add-ons
              </Typography>
              <Stack spacing={1.25} sx={{ mb: 1 }}>
                {availableOneTimeAddons.map((addon) => {
                  const selectedQuantity = oneTimeAddonSelections.find((entry) => entry.addon_key === addon.key)?.quantity || 0;
                  const maxPerOrder = addon.max_per_order || 1;
                  const isSelected = selectedQuantity > 0;

                  return (
                    <Box key={addon.key} sx={{ p: 1.25, border: '1px solid', borderColor: isSelected ? 'warning.main' : 'divider', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {addon.name}
                            {isSelected && <CheckCircleOutline color="warning" sx={{ fontSize: 16 }} />}
                          </Typography>
                          {addon.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {addon.description}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            ${(addon.price_cents / 100).toFixed(2)} one-time{maxPerOrder === 1 ? ' · max 1/order' : ` · max ${maxPerOrder}/order`}
                          </Typography>
                        </Box>
                        {maxPerOrder === 1 ? (
                          <Button
                            size="small"
                            variant={isSelected ? 'contained' : 'outlined'}
                            color="warning"
                            onClick={() => handleOneTimeAddonQuantityChange(addon.key, isSelected ? 0 : 1)}
                          >
                            {isSelected ? 'Added' : 'Add'}
                          </Button>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" onClick={() => handleOneTimeAddonQuantityChange(addon.key, selectedQuantity - 1)} disabled={selectedQuantity <= 0}>
                              <Remove fontSize="small" />
                            </IconButton>
                            <TextField
                              size="small"
                              type="number"
                              value={selectedQuantity}
                              onChange={(event) => handleOneTimeAddonQuantityChange(addon.key, Number(event.target.value))}
                              inputProps={{ min: 0, max: maxPerOrder, step: 1 }}
                              sx={{ width: 76 }}
                            />
                            <IconButton size="small" onClick={() => handleOneTimeAddonQuantityChange(addon.key, selectedQuantity + 1)} disabled={selectedQuantity >= maxPerOrder}>
                              <Add fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
              {oneTimeAddonsTotalCents > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">Selected one-time add-ons</Typography>
                  <Typography variant="body2">${(oneTimeAddonsTotalCents / 100).toFixed(2)}</Typography>
                </Box>
              )}
            </>
          )}

          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Monthly total</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatMonthly(totalPriceCents)}</Typography>
            </Box>
            {(oneTimeAddonsTotalCents > 0 || additionalUserOneTimeTotalCents > 0) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                <Typography variant="body2" color="text.secondary">Due today</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>${((oneTimeAddonsTotalCents + additionalUserOneTimeTotalCents) / 100).toFixed(2)}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <BuyBox
        service={service as any}
        selectedVariants={selectedVariantSelections}
        selectedVariantPrice={totalPriceCents}
        selectedVariantKey={selectedVariant}
        selectedTerm={selectedTerm}
        serviceConfiguration={normalizedServiceConfiguration}
      />
    </Grid>
  );
};

export default React.memo(ServiceBuyBox);
